import { Archive, Eye, Save2, ArchiveAdd } from 'iconsax-react';
import { ChevronDown, Pin, XCircle } from 'lucide-react';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@src/design-system/popover';
import { ViewsPopoverContent } from '@shared/features/views/components/views-popover-content';
import { useTableStates } from '../hooks/use-table-states';
import { useTableViewHelpers } from '../hooks/use-table-view-helpers';
import { CreateViewPopover } from '@src/shared/features/views/components/create-view-popover';
import { Button } from '@src/design-system/button';
import { Spinner } from '@src/design-system/spinner';
import viewApi from '@src/shared/features/views/services/view-api';
import { ButtonGroup } from '@src/design-system/button-group';

const CurrentViewPreview = () => {
  const { currentView, setCurrentView } = useTableStates();

  const handleClear = () => {
    setCurrentView(undefined);
  };

  return (
    <div className='flex items-center'>
      <button
        onClick={handleClear}
        className='border border-pale-border border-e-0 rounded-e-none group text-gray-500 rounded-lg px-2.5 cursor-pointer h-10 flex items-center justify-center'
        title='Clear view'
      >
        <XCircle className='group-hover:opacity-100 size-4 opacity-70 transition-opacity text-primary-1000' />
      </button>

      <Popover>
        <PopoverTrigger asChild>
          <button className='bg-primary-30 max-w-[250px] rounded-s-none hover:bg-primary-50 border border-primary-200 text-primary-1000 text-sm px-2.5 h-10 rounded-lg flex items-center justify-between gap-2 cursor-pointer transition-colors select-none'>
            <Eye
              variant='Bold'
              className='shrink-0 size-5'
            />
            <span
              title={currentView?.name}
              className='font-medium truncate'
            >
              {currentView?.name}
            </span>

            {currentView?.is_default && (
              <Pin className='size-4 fill-current text-amber-500 text-amber-500 rotate-45 shrink-0' />
            )}

            <ChevronDown className='size-4 text-primary-1000 shrink-0' />
          </button>
        </PopoverTrigger>

        <PopoverContent
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          align='end'
          className='p-0 w-[350px] border border-pale-border bg-white rounded-lg'
        >
          <ViewsPopoverContent />
        </PopoverContent>
      </Popover>
    </div>
  );
};

const SaveNewViewPreview = () => {
  const { resetFiltersToOriginalViewFilters } = useTableViewHelpers();

  return (
    <div className='flex items-center'>
      <button
        onClick={resetFiltersToOriginalViewFilters}
        className='border border-pale-border border-e-0 rounded-e-none group text-gray-500 rounded-lg px-2.5 cursor-pointer h-10 flex items-center justify-center'
        title='Clear view'
      >
        <XCircle className='group-hover:opacity-100 size-4 opacity-70 transition-opacity text-primary-1000' />
      </button>

      <CreateViewPopover
        trigger={
          <Button className='bg-primary-30 [&_svg]:size-5 gap-2 rounded-s-none hover:bg-primary-50 border border-primary-200 text-primary-1000 text-sm px-2.5 h-10'>
            <ArchiveAdd className='shrink-0' />
            Create View
          </Button>
        }
      />
    </div>
  );
};

const UpdateCurrentViewPreview = () => {
  const { resetFiltersToOriginalViewFilters } = useTableViewHelpers();
  const { currentView, tableFilters } = useTableStates();

  const { mutate: updateView, isPending } = viewApi.useUpdate();

  return (
    <div className='flex items-center'>
      <ButtonGroup className='border h-10 overflow-hidden border-pale-border rounded-lg'>
        <button
          onClick={resetFiltersToOriginalViewFilters}
          className='group text-gray-500 border-e border-pale-border h-full px-2.5 cursor-pointer flex items-center justify-center'
          title='Clear view'
        >
          <XCircle className='group-hover:opacity-100 size-4 opacity-70 transition-opacity text-primary-1000' />
        </button>

        <Button
          className='bg-primary-30 [&_svg]:size-[18px] gap-2 hover:bg-primary-100/70 border-e border-primary-200 text-primary-1000 text-sm h-full px-2.5'
          onClick={() => {
            if (isPending) return;
            updateView({
              id: currentView!.id,
              static_filters: tableFilters?.staticFilters || [],
              filters: tableFilters?.dynamicFilters?.map((d) => d.id) || [],
              custom_filters:
                tableFilters?.customFieldFilters?.map((d) => d.id) || [],
            });
          }}
        >
          {isPending ? <Spinner /> : <Archive />}
          Update View
        </Button>

        <CreateViewPopover
          trigger={
            <Button className='bg-primary-30 [&_svg]:size-[18px] gap-2 hover:bg-primary-100/70 border-e-0 text-primary-1000 text-sm h-full px-2.5 rounded-s-none'>
              <ArchiveAdd className='shrink-0' />
              Create View
            </Button>
          }
        />
      </ButtonGroup>
    </div>
  );
};

const ViewsList = () => {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className='border border-pale-border hover:bg-primary-100/70 bg-primary-50 text-gray-800 text-sm px-4 py-2.5 rounded-lg flex items-center gap-2 cursor-pointer transition-colors h-10 select-none'>
          <Save2
            variant='Bulk'
            size={20}
          />
          <span>Saved Views</span>
          <ChevronDown className='size-4 text-gray-500' />
        </button>
      </PopoverTrigger>

      <PopoverContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        align='start'
        className='p-0 w-[350px] border border-pale-border bg-white rounded-lg shadow-none'
      >
        <ViewsPopoverContent />
      </PopoverContent>
    </Popover>
  );
};

// State → component mapping. Adding a 5th state = one new entry in this map.
const STATE_COMPONENTS = {
  'view+dirty': UpdateCurrentViewPreview,
  'view+clean': CurrentViewPreview,
  'no-view+dirty': SaveNewViewPreview,
  'no-view+clean': ViewsList,
} as const;

type StateKey = keyof typeof STATE_COMPONENTS;

export function SavedViewsControl() {
  const { currentView } = useTableStates();
  const { isDirty } = useTableViewHelpers();

  const hasView = !!currentView?.id;
  const stateKey: StateKey = `${hasView ? 'view' : 'no-view'}+${isDirty ? 'dirty' : 'clean'}`;
  const Component = STATE_COMPONENTS[stateKey];

  return <Component />;
}
